/**
 * Timeline Aggregation Service
 * 
 * This service handles the aggregation of timeline data from all sites
 * to create a comprehensive view of megalithic construction periods
 * across different cultures and time periods.
 */

import { collection, getDocs, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { parseEra, compareEras, displayEra } from '../utils/eraUtils';

/**
 * Fetches all sites and extracts their timeline data
 * @returns {Promise<Object>} Object containing aggregated timeline data
 */
export const aggregateAllTimelines = async () => {
  try {
    console.log('Starting timeline aggregation from all sites...');
    
    // Fetch all sites from Firestore
    const sitesRef = collection(db, 'sites');
    const sitesSnap = await getDocs(sitesRef);
    
    const timelineData = {
      totalSites: 0,
      sitesWithTimelines: 0,
      allPeriods: [],
      periodsByEra: {},
      periodsByCulture: {},
      periodsByRegion: {},
      timelineRange: {
        earliest: null,
        latest: null
      },
      lastUpdated: new Date().toISOString()
    };

    // Process each site
    sitesSnap.docs.forEach((doc) => {
      const siteData = doc.data();
      const siteId = doc.id;
      
      timelineData.totalSites++;
      
      // Check if site has construction periods
      if (siteData.constructionPeriods && Array.isArray(siteData.constructionPeriods) && siteData.constructionPeriods.length > 0) {
        timelineData.sitesWithTimelines++;
        
        // Process each construction period
        siteData.constructionPeriods.forEach((period, index) => {
          const periodData = {
            siteId,
            siteName: siteData.name || 'Unknown Site',
            siteLocation: {
              region: siteData.location?.region || 'Unknown Region',
              country: siteData.location?.country || 'Unknown Country',
              coordinates: siteData.location?.coordinates || null
            },
            periodIndex: index,
            periodName: period.periodName || `Period ${index + 1}`,
            startEra: period.startEra || '',
            endEra: period.endEra || '',
            startEraApproximate: period.startEraApproximate || false,
            endEraApproximate: period.endEraApproximate || false,
            culture: period.culture || siteData.culture || 'Unknown Culture',
            features: period.features || [],
            details: period.details || '',
            isMainPeriod: index === (siteData.mainPeriodIndex || 0)
          };

          // Add to all periods array
          timelineData.allPeriods.push(periodData);

          // Update timeline range
          if (periodData.startEra) {
            const startParsed = parseEra(periodData.startEra);
            if (startParsed && startParsed.value !== null) {
              if (timelineData.timelineRange.earliest === null || 
                  compareEras(periodData.startEra, timelineData.timelineRange.earliest) < 0) {
                timelineData.timelineRange.earliest = periodData.startEra;
              }
            }
          }

          if (periodData.endEra) {
            const endParsed = parseEra(periodData.endEra);
            if (endParsed && endParsed.value !== null) {
              if (timelineData.timelineRange.latest === null || 
                  compareEras(periodData.endEra, timelineData.timelineRange.latest) > 0) {
                timelineData.timelineRange.latest = periodData.endEra;
              }
            }
          }

          // Group by era (broad time periods)
          const eraCategory = categorizeByEra(periodData.startEra);
          if (!timelineData.periodsByEra[eraCategory]) {
            timelineData.periodsByEra[eraCategory] = [];
          }
          timelineData.periodsByEra[eraCategory].push(periodData);

          // Group by culture
          const culture = periodData.culture;
          if (!timelineData.periodsByCulture[culture]) {
            timelineData.periodsByCulture[culture] = [];
          }
          timelineData.periodsByCulture[culture].push(periodData);

          // Group by region
          const region = periodData.siteLocation.region;
          if (!timelineData.periodsByRegion[region]) {
            timelineData.periodsByRegion[region] = [];
          }
          timelineData.periodsByRegion[region].push(periodData);
        });
      }
    });

    // Sort all periods chronologically
    timelineData.allPeriods.sort((a, b) => {
      const startComparison = compareEras(a.startEra, b.startEra);
      if (startComparison !== 0) return startComparison;
      return compareEras(a.endEra, b.endEra);
    });

    // Sort periods within each grouping
    Object.keys(timelineData.periodsByEra).forEach(era => {
      timelineData.periodsByEra[era].sort((a, b) => compareEras(a.startEra, b.startEra));
    });

    Object.keys(timelineData.periodsByCulture).forEach(culture => {
      timelineData.periodsByCulture[culture].sort((a, b) => compareEras(a.startEra, b.startEra));
    });

    Object.keys(timelineData.periodsByRegion).forEach(region => {
      timelineData.periodsByRegion[region].sort((a, b) => compareEras(a.startEra, b.startEra));
    });

    console.log(`Timeline aggregation complete: ${timelineData.sitesWithTimelines}/${timelineData.totalSites} sites with timeline data`);
    console.log(`Total periods found: ${timelineData.allPeriods.length}`);
    console.log(`Timeline range: ${timelineData.timelineRange.earliest} to ${timelineData.timelineRange.latest}`);

    return timelineData;
  } catch (error) {
    console.error('Error aggregating timelines:', error);
    throw error;
  }
};

/**
 * Categorizes a period by broad historical era
 * @param {string} startEra - The start era string
 * @returns {string} Era category
 */
const categorizeByEra = (startEra) => {
  if (!startEra) return 'Unknown';
  
  const parsed = parseEra(startEra);
  if (!parsed || parsed.value === null) return 'Unknown';
  
  const year = parsed.value;
  
  if (typeof year === 'symbol') return 'Modern'; // Present
  
  if (year < -10000) return 'Paleolithic';
  if (year < -8000) return 'Mesolithic';
  if (year < -3000) return 'Neolithic';
  if (year < -1200) return 'Bronze Age';
  if (year < 500) return 'Iron Age';
  if (year < 1500) return 'Medieval';
  return 'Modern';
};

/**
 * Saves aggregated timeline data to Firestore for caching
 * @param {Object} timelineData - The aggregated timeline data
 */
export const saveAggregatedTimelines = async (timelineData) => {
  try {
    const timelineRef = doc(db, 'system', 'aggregated_timelines');
    await setDoc(timelineRef, timelineData);
    console.log('Aggregated timeline data saved to Firestore');
  } catch (error) {
    console.error('Error saving aggregated timeline data:', error);
    throw error;
  }
};

/**
 * Loads cached aggregated timeline data from Firestore
 * @returns {Promise<Object|null>} Cached timeline data or null if not found
 */
export const loadCachedTimelines = async () => {
  try {
    const timelineRef = doc(db, 'system', 'aggregated_timelines');
    const timelineSnap = await getDoc(timelineRef);
    
    if (timelineSnap.exists()) {
      const data = timelineSnap.data();
      console.log('Loaded cached timeline data from:', data.lastUpdated);
      return data;
    }
    
    return null;
  } catch (error) {
    console.error('Error loading cached timeline data:', error);
    return null;
  }
};

/**
 * Performs initial load of all timelines with caching
 * @param {boolean} forceRefresh - Whether to force a refresh from source data
 * @returns {Promise<Object>} Aggregated timeline data
 */
export const initialTimelineLoad = async (forceRefresh = false) => {
  try {
    // Try to load cached data first (unless forcing refresh)
    if (!forceRefresh) {
      const cachedData = await loadCachedTimelines();
      if (cachedData) {
        // Check if cache is recent (less than 24 hours old)
        const cacheAge = Date.now() - new Date(cachedData.lastUpdated).getTime();
        const maxCacheAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        
        if (cacheAge < maxCacheAge) {
          console.log('Using cached timeline data (age:', Math.round(cacheAge / (60 * 60 * 1000)), 'hours)');
          return cachedData;
        }
      }
    }

    // Aggregate fresh data
    console.log('Aggregating fresh timeline data...');
    const timelineData = await aggregateAllTimelines();
    
    // Save to cache
    await saveAggregatedTimelines(timelineData);
    
    return timelineData;
  } catch (error) {
    console.error('Error in initial timeline load:', error);
    throw error;
  }
};

/**
 * Gets timeline statistics
 * @param {Object} timelineData - Aggregated timeline data
 * @returns {Object} Timeline statistics
 */
export const getTimelineStats = (timelineData) => {
  if (!timelineData) return null;

  return {
    totalSites: timelineData.totalSites,
    sitesWithTimelines: timelineData.sitesWithTimelines,
    totalPeriods: timelineData.allPeriods.length,
    coveragePercentage: Math.round((timelineData.sitesWithTimelines / timelineData.totalSites) * 100),
    eraDistribution: Object.keys(timelineData.periodsByEra).map(era => ({
      era,
      count: timelineData.periodsByEra[era].length
    })).sort((a, b) => b.count - a.count),
    cultureDistribution: Object.keys(timelineData.periodsByCulture).map(culture => ({
      culture,
      count: timelineData.periodsByCulture[culture].length
    })).sort((a, b) => b.count - a.count),
    regionDistribution: Object.keys(timelineData.periodsByRegion).map(region => ({
      region,
      count: timelineData.periodsByRegion[region].length
    })).sort((a, b) => b.count - a.count),
    timelineRange: timelineData.timelineRange
  };
};

/**
 * Filters timeline data by various criteria
 * @param {Object} timelineData - Aggregated timeline data
 * @param {Object} filters - Filter criteria
 * @returns {Object} Filtered timeline data
 */
export const filterTimelineData = (timelineData, filters = {}) => {
  if (!timelineData) return null;

  let filteredPeriods = [...timelineData.allPeriods];

  // Filter by era
  if (filters.era) {
    filteredPeriods = filteredPeriods.filter(period => 
      categorizeByEra(period.startEra) === filters.era
    );
  }

  // Filter by culture
  if (filters.culture) {
    filteredPeriods = filteredPeriods.filter(period => 
      period.culture.toLowerCase().includes(filters.culture.toLowerCase())
    );
  }

  // Filter by region
  if (filters.region) {
    filteredPeriods = filteredPeriods.filter(period => 
      period.siteLocation.region.toLowerCase().includes(filters.region.toLowerCase())
    );
  }

  // Filter by country
  if (filters.country) {
    filteredPeriods = filteredPeriods.filter(period => 
      period.siteLocation.country.toLowerCase().includes(filters.country.toLowerCase())
    );
  }

  // Filter by date range
  if (filters.startEra || filters.endEra) {
    filteredPeriods = filteredPeriods.filter(period => {
      if (filters.startEra && compareEras(period.endEra || period.startEra, filters.startEra) < 0) {
        return false;
      }
      if (filters.endEra && compareEras(period.startEra, filters.endEra) > 0) {
        return false;
      }
      return true;
    });
  }

  // Filter by main periods only
  if (filters.mainPeriodsOnly) {
    filteredPeriods = filteredPeriods.filter(period => period.isMainPeriod);
  }

  return {
    ...timelineData,
    allPeriods: filteredPeriods,
    filteredCount: filteredPeriods.length
  };
};